import React, { useState, useEffect, useCallback } from 'react';
import { debounce } from 'lodash';

function Calendar({ onDateChange, initialStartDate, initialEndDate, className = "" }) {
    // Helper function to get last month's date range as fallback
    const getLastMonthDateRange = () => {
        const now = new Date();
        // Get the first day of current month
        const firstDayOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        // Get the last day of previous month (day 0 of current month)
        const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        // Get the first day of previous month
        const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        
        return {
            startDate: firstDayOfLastMonth.toISOString().split('T')[0],
            endDate: lastDayOfLastMonth.toISOString().split('T')[0]
        };
    };

    const lastMonthRange = getLastMonthDateRange();
    
    // Load saved dates from localStorage or use defaults
    const getSavedDates = () => {
        if (typeof window !== 'undefined') {
            try {
                const savedDates = localStorage.getItem('selectedDateRange');
                if (savedDates) {
                    const parsed = JSON.parse(savedDates);
                    return {
                        startDate: parsed.startDate || initialStartDate || lastMonthRange.startDate,
                        endDate: parsed.endDate || initialEndDate || lastMonthRange.endDate
                    };
                }
            } catch (error) {
                console.error('Error loading saved dates:', error);
            }
        }
        return {
            startDate: initialStartDate || lastMonthRange.startDate,
            endDate: initialEndDate || lastMonthRange.endDate
        };
    };
    
    const savedDates = getSavedDates();
    const [startDate, setStartDate] = useState(savedDates.startDate);
    const [endDate, setEndDate] = useState(savedDates.endDate);

    // Update internal state when props change
    useEffect(() => {
        if (initialStartDate) {
            setStartDate(initialStartDate);
        }
        if (initialEndDate) {
            setEndDate(initialEndDate);
        }
    }, [initialStartDate, initialEndDate]);

    // Debounced API call function
    const debouncedDateChange = useCallback(
        debounce((dates) => {
            if (onDateChange) {
                onDateChange(dates);
            }
        }, 800), // 800ms delay
        [onDateChange]
    );

    // Trigger initial date change on mount
    useEffect(() => {
        if (onDateChange && startDate && endDate) {
            onDateChange({ startDate, endDate });
        }
    }, []);

    const saveDatesToStorage = (start, end) => {
        if (typeof window !== 'undefined') {
            try {
                localStorage.setItem('selectedDateRange', JSON.stringify({
                    startDate: start,
                    endDate: end
                }));
            } catch (error) {
                console.error('Error saving dates:', error);
            }
        }
    };

    const handleStartChange = (e) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        saveDatesToStorage(newStartDate, endDate);
        debouncedDateChange({ startDate: newStartDate, endDate });
    };

    const handleEndChange = (e) => {
        const newEndDate = e.target.value;
        setEndDate(newEndDate);
        saveDatesToStorage(startDate, newEndDate);
        debouncedDateChange({ startDate, endDate: newEndDate });
    };

    return (
        <div className={`flex items-center gap-3 text-sm bg-gray-50 border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 ${className}`}>
            <input
                type="date"
                className="outline-none bg-gray-50 rounded-md px-3 py-2 font-normal text-gray-700 min-w-[140px] hover:bg-white transition-colors duration-200"
                value={startDate}
                onChange={handleStartChange}
                max={endDate}
            />
            <div className="flex items-center justify-center size-6">
                <svg 
                    className="w-4 h-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth="2" 
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                </svg>
            </div>
            <input
                type="date"
                className="outline-none bg-gray-50 rounded-md px-3 py-2 font-normal text-gray-700 min-w-[140px] hover:bg-white transition-colors duration-200"
                value={endDate}
                onChange={handleEndChange}
                min={startDate}
            />
        </div>
    );
}

export default Calendar;